import { useState } from 'react';
import { Sparkles, AlertTriangle, ShieldCheck, CircleAlert } from 'lucide-react';
import { supabase } from '@/lib/supabase';

// ── ASC AI surfaces (2026-08-15) ─────────────────────────────────────────────
// Every surface here is PHYSICIAN-INITIATED (nothing runs on page load), reads
// only real documented data through the asc-ai edge function (caller-JWT +
// RLS; ANTHROPIC_API_KEY is server-side only), and renders honest empty
// states when the record has nothing to say. AI output is decision support —
// it never writes to the clinical record.

export async function invokeAscAi<T = Record<string, unknown>>(payload: Record<string, unknown>): Promise<T> {
    const { data, error } = await supabase.functions.invoke('asc-ai', { body: payload });
    if (error) throw new Error(error.message ?? 'AI request failed');
    if ((data as { error?: string })?.error) throw new Error(String((data as { error: string; detail?: string }).detail ?? (data as { error: string }).error));
    return data as T;
}

export const aiPanelShell = 'rounded-xl border border-violet-500/30 bg-violet-500/[0.04]';
export const aiHeaderBadge = (
    <span className="text-[9px] uppercase tracking-[0.14em] rounded border border-violet-400/40 bg-violet-500/10 text-violet-300 px-1.5 py-0.5">AI · decision support</span>
);

interface DayBrief {
    headline?: string;
    sequencing?: string[];
    coverage_gaps?: string[];
    pacu_pressure?: string;
    watch_items?: string[];
}

export function DayBriefPanel({ dateStr, facilityId }: { dateStr: string; facilityId: number }) {
    const [state, setState] = useState<'idle' | 'loading' | 'done' | 'empty' | 'error'>('idle');
    const [brief, setBrief] = useState<DayBrief | null>(null);
    const [message, setMessage] = useState('');

    async function run() {
        setState('loading');
        try {
            const res = await invokeAscAi<{ ok: boolean; empty?: boolean; message?: string; brief?: DayBrief }>({
                action: 'day-brief', date: dateStr, facility_id: facilityId,
            });
            if (res.empty) { setMessage(res.message ?? 'Nothing to brief.'); setState('empty'); return; }
            setBrief(res.brief ?? null);
            setState('done');
        } catch (e) {
            setMessage(e instanceof Error ? e.message : 'AI day brief unavailable');
            setState('error');
        }
    }

    return (
        <section className={`${aiPanelShell} p-4`}>
            <div className="flex flex-wrap items-center gap-3">
                <Sparkles size={15} className="text-violet-300" />
                <div className="mr-auto">
                    <div className="flex items-center gap-2"><h3 className="text-sm font-medium text-pt-text">Operational day brief</h3>{aiHeaderBadge}</div>
                    <p className="text-[10px] text-pt-text-muted">Sequencing, coverage, and PACU pressure — from today's booked cases and named staffing only.</p>
                </div>
                <button type="button" onClick={() => void run()} disabled={state === 'loading'}
                    className="text-xs rounded border border-violet-400/40 text-violet-200 px-3 py-1.5 hover:bg-violet-500/10 disabled:opacity-50">
                    {state === 'loading' ? 'Reading the schedule…' : state === 'done' ? 'Regenerate' : 'Generate brief'}
                </button>
            </div>
            {state === 'empty' && <p className="mt-3 text-xs text-pt-text-muted">{message}</p>}
            {state === 'error' && <p className="mt-3 text-xs text-red-400 flex items-center gap-1.5"><CircleAlert size={12} /> {message}</p>}
            {state === 'done' && brief && (
                <div className="mt-3 space-y-2.5 text-xs text-pt-text">
                    {brief.headline && <p className="text-sm">{brief.headline}</p>}
                    {!!brief.sequencing?.length && <BriefList label="Sequencing" items={brief.sequencing} />}
                    {!!brief.coverage_gaps?.length && <BriefList label="Coverage gaps" items={brief.coverage_gaps} tone="warn" />}
                    {brief.pacu_pressure && <p><span className="text-pt-text-muted">PACU pressure:</span> {brief.pacu_pressure}</p>}
                    {!!brief.watch_items?.length && <BriefList label="Watch" items={brief.watch_items} tone="warn" />}
                </div>
            )}
        </section>
    );
}

function BriefList({ label, items, tone }: { label: string; items: string[]; tone?: 'warn' }) {
    return (
        <div>
            <div className={`text-[10px] uppercase tracking-wider ${tone === 'warn' ? 'text-amber-400' : 'text-pt-text-muted'}`}>{label}</div>
            <ul className="mt-0.5 space-y-0.5">{items.map((s, i) => <li key={i} className="flex gap-1.5"><span className="text-pt-text-dim">·</span><span>{s}</span></li>)}</ul>
        </div>
    );
}

interface ReadinessBrief {
    readiness?: 'ready' | 'caution' | 'defer_to_physician';
    summary?: string;
    documented?: { item: string; value: string; note?: string }[];
    not_documented?: string[];
    flags?: string[];
}

export function OrReadinessBrief({ caseId }: { caseId: number }) {
    const [state, setState] = useState<'idle' | 'loading' | 'done' | 'empty' | 'error'>('idle');
    const [brief, setBrief] = useState<ReadinessBrief | null>(null);
    const [message, setMessage] = useState('');

    async function run() {
        setState('loading');
        try {
            const res = await invokeAscAi<{ ok: boolean; empty?: boolean; message?: string; brief?: ReadinessBrief }>({
                action: 'or-readiness', case_id: caseId,
            });
            if (res.empty) { setMessage(res.message ?? 'No documented risk data yet.'); setState('empty'); return; }
            setBrief(res.brief ?? null);
            setState('done');
        } catch (e) {
            setMessage(e instanceof Error ? e.message : 'Readiness brief unavailable');
            setState('error');
        }
    }

    const tone = brief?.readiness === 'ready' ? { icon: <ShieldCheck size={14} className="text-emerald-400" />, cls: 'text-emerald-400', label: 'Ready — physician confirms' }
        : brief?.readiness === 'caution' ? { icon: <AlertTriangle size={14} className="text-amber-400" />, cls: 'text-amber-400', label: 'Caution — documented findings' }
            : { icon: <CircleAlert size={14} className="text-pt-text-muted" />, cls: 'text-pt-text-muted', label: 'Physician determination required' };

    return (
        <section className={`${aiPanelShell} p-4 md:col-span-2 lg:col-span-3`}>
            <div className="flex flex-wrap items-center gap-3">
                <Sparkles size={15} className="text-violet-300" />
                <div className="mr-auto">
                    <div className="flex items-center gap-2"><h3 className="text-sm font-medium text-pt-text">OR-readiness brief</h3>{aiHeaderBadge}</div>
                    <p className="text-[10px] text-pt-text-muted">From the documented anesthesia pre-op, surgical consultation, and risk scores. The physician determines readiness.</p>
                </div>
                <button type="button" onClick={() => void run()} disabled={state === 'loading'}
                    className="text-xs rounded border border-violet-400/40 text-violet-200 px-3 py-1.5 hover:bg-violet-500/10 disabled:opacity-50">
                    {state === 'loading' ? 'Reading the record…' : state === 'done' ? 'Regenerate' : 'Generate brief'}
                </button>
            </div>
            {state === 'empty' && <p className="mt-3 text-xs text-pt-text-muted">{message}</p>}
            {state === 'error' && <p className="mt-3 text-xs text-red-400 flex items-center gap-1.5"><CircleAlert size={12} /> {message}</p>}
            {state === 'done' && brief && (
                <div className="mt-3 space-y-2.5 text-xs text-pt-text">
                    <div className={`flex items-center gap-1.5 text-sm font-medium ${tone.cls}`}>{tone.icon}{tone.label}</div>
                    {brief.summary && <p>{brief.summary}</p>}
                    {!!brief.documented?.length && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                            {brief.documented.map((d, i) => (
                                <div key={i} className="rounded border border-pt-border/60 bg-pt-darker/50 px-2.5 py-1.5">
                                    <span className="text-pt-text-muted">{d.item}:</span> {d.value}{d.note ? <span className="text-pt-text-dim"> — {d.note}</span> : null}
                                </div>
                            ))}
                        </div>
                    )}
                    {!!brief.flags?.length && <BriefList label="Documented flags" items={brief.flags} tone="warn" />}
                    {!!brief.not_documented?.length && <BriefList label="Not yet documented" items={brief.not_documented} />}
                </div>
            )}
        </section>
    );
}
